import { computed, toRaw } from "vue"
import { apiClient } from "@/services/api"
import { hasMoreRows } from "@/services/listAnswer"
import { downloadFileFromXHR } from "@/services/file"
import { dateToNeutralString } from "@/services/date"
import { defineStore } from "pinia"
import { useEmployeeStore } from "@/store/employee"
import { PERMISSIONS } from "@/services/permission"
import { hasPermissionsOfAtLeastOne } from "@/services/permissionService"

/**
 * The sample week's builders, loaded by [enterHelpDemo] and by nothing else. A dynamic import, like
 * the sandbox and the sample world in `main.js`: only the instance inside the Erklärung's frame ever
 * needs them, and a static import shipped the whole sample week to every session that opened the
 * Zeiterfassung. Every read below happens behind `helpDemo`, which is only set once this has loaded.
 */
let demo: typeof import("@/services/viewHelp/demo") | null = null
const demoData = () => {
  if (!demo) throw new Error("[timetracking] the help demo is read before enterHelpDemo loaded it")
  return demo
}

/** Keep in sync with the backend `com.lewara.backend.domain.timeentry.TimeEntryStatus`. */
export type TimeEntryStatus = "OPEN" | "SUBMITTED" | "APPROVED" | "REJECTED"

/** Keep in sync with the backend `com.lewara.backend.domain.timeentry.TimeEntrySource`. */
export type TimeEntrySource = "SELF" | "CREW" | "IMPORT"

/** Keep in sync with the backend `com.lewara.backend.domain.timeentry.TimeEntryCaptureChannel`. */
export type TimeEntryCaptureChannel = "WEB" | "MOBILE" | "UNKNOWN"

export interface TimeEntryCaptureLocation {
  latitude: number
  longitude: number
  accuracyMeters: number | null
}

/**
 * Why the app sent no location with a stamp: the permission was refused, location services were off, or
 * the device found no fix in time. Keep in sync with the backend.
 */
export type TimeEntryLocationMissing = "DENIED" | "OFF" | "UNAVAILABLE"

/**
 * Where and through which client a day was recorded — stamped by the server, never sent by this app
 * except as the `X-Lewara-Client: WEB` header. `location` is always null for a web recording, and null
 * for a viewer who is neither the employee nor allowed to read other people's time.
 */
export interface TimeEntryCapture {
  channel: TimeEntryCaptureChannel
  /** The server's clock when the request arrived. */
  capturedAt: string
  /**
   * The device's clock at the tap, where the app sent one. Earlier than `capturedAt` by however long the
   * stamp waited on the phone for a connection, or off by however far the phone's clock is; absent for web
   * recordings and from older backends.
   */
  stampedAt?: string | null
  location: TimeEntryCaptureLocation | null
  /** Set only where the app said why it sent no location. */
  locationMissing?: TimeEntryLocationMissing | null
}

export interface TimeEntryBreak {
  fromTime: string
  toTime: string
}

export interface TimeEntry {
  id: string
  userId: string
  userName: string | null
  date: string
  fromTime: string
  /** Null while a clock-in is still open. */
  toTime: string | null
  breaks: TimeEntryBreak[]
  /**
   * When the break being taken right now began, on a day that is still running — null on every
   * closed day. Nothing is counted from it: an unfinished break has no duration yet, and the panel
   * reads it only as "in Pause seit 12:04".
   */
  openBreakFrom: string | null
  projectId: string | null
  projectName: string | null
  phaseId: string | null
  positionId: string | null
  workTimeModelId: string | null
  shiftTemplateId: string | null
  source: TimeEntrySource
  status: TimeEntryStatus
  note: string | null
  recordedBy: string | null
  /** `recordedBy`'s display name; absent from backends that send only the id. */
  recordedByName?: string | null
  /** Written when the day was created; null for days recorded before capturing existed. Never changed by an edit. */
  capture: TimeEntryCapture | null
  /** Written by the clock-out that finished a running day — which may have happened on another client. */
  clockOutCapture: TimeEntryCapture | null
  /**
   * Computed server-side, deliberately: `breakHours` is what is actually *deducted* — the larger of
   * what was typed and what the working-time model requires — so the page cannot disagree with what
   * the server counts everywhere else.
   */
  grossHours: string
  breakHours: string
  workedHours: string
  /** Null where no working-time model was assigned on that date. */
  targetHours: string | null
  balanceHours: string | null
  /**
   * Why the day was turned down — or the sentence attached to approving or reopening it.
   *
   * Its own field, never `note`: that one is the employee's account of the day, and overwriting it
   * to comment on it would destroy the thing being reviewed. Cleared by the edit that answers it.
   */
  reviewNote: string | null
  reviewedAt: string | null
  reviewedBy: string | null
  createdAt: string
  lastModifiedAt: string | null
  lastModifiedBy: string | null
}

/** Why the recording form was prefilled with a project — the SPA turns this into a hint. */
export type SuggestionSource = "PLANNING" | "SOLE_MEMBERSHIP" | "NONE"

export interface TimeEntrySuggestion {
  projectId: string | null
  projectName: string | null
  phaseId: string | null
  source: SuggestionSource
  workTimeModelId: string | null
  plannedFrom: string | null
  plannedTo: string | null
  targetHours: string | null
}

/**
 * One project the caller may book their own day to — `GET /time-tracking/me/projects`. Id and name
 * only: that is all a picker shows, and a role that records time without any project right must
 * not learn more about a site than its name.
 *
 * Keep in sync with the backend `FrontendBookableProjectResponse`.
 */
export interface BookableProject {
  id: string
  name: string
}

/**
 * The two rights that decide which projects the caller may book their own time to: assigned ones,
 * foreign ones or both. Neither leaves only the sites the personnel planning put them on, which the
 * server allows whatever the rights say — so "no right" is not the same as "no project".
 */
export const BOOK_OWN_TIME_PERMISSIONS = [
  PERMISSIONS.BOOK_OWN_TIME_ASSIGNED_PROJECT,
  PERMISSIONS.BOOK_OWN_TIME_FOREIGN_PROJECT,
]

/**
 * Whether the project field belongs on screen at all: either a booking right, or a list that came
 * back with something in it.
 *
 * The second half is not belt and braces — the server also allows the sites the personnel planning
 * put the caller on, whatever their rights say, and the SPA has no way to know about those except by
 * what `/me/projects` answers. Hiding the field on the rights alone would take the picker away from
 * exactly the crews this widening is for, which is why [projects] is a required argument: a call
 * without it would compile and answer the old, rights-only question.
 *
 * A composable rather than a plain function, and the permission half is read **once**:
 * `hasPermissionsOfAtLeastOne` builds a fresh computed per call (one more per permission inside it),
 * and this is re-evaluated on every change to a list that has no bearing on what the role may do.
 */
export const useCanBookOwnTimeToProjects = (projects: () => BookableProject[]) => {
  const hasBookingRight = hasPermissionsOfAtLeastOne(BOOK_OWN_TIME_PERMISSIONS)
  return computed(() => hasBookingRight.value || projects().length > 0)
}

/**
 * Keep in sync with the backend `com.lewara.backend.domain.personnelassignment.AssignedAbsenceSpan`.
 *
 * Absences are no longer fetched by the SPA at all. `GET /time-tracking/absence-days` exists and is
 * still the raw feed, but the decision it was fetched *for* — whether an absence excuses an empty
 * day — is reached server-side now and arrives already applied on each [TimeCoverageDay]. Only a
 * whole day away excuses one, and having that rule in the browser meant having it twice: the
 * Monatsnachweis and the missing-days notification need the same verdict and neither is a browser.
 */
export type AbsenceSpan = "FULLDAY" | "HALFDAY" | "CUSTOM"

/**
 * What one day of one employee's calendar says about recorded time — the verdict `GET
 * /time-tracking/coverage` reaches, in the order that makes it right.
 *
 * Resolved server-side, and it used to be assembled here from three separate loads. That is why the
 * grid only ever worked for a *single* selected employee, and why the rule existed twice: the
 * Monatsnachweis and the notification monitor need the same decision and neither of them is a
 * browser. Keep in sync with the backend `TimeCoverageState`.
 */
export type TimeCoverageState = "RECORDED" | "RUNNING" | "ABSENT" | "NO_MODEL" | "OFF" | "FUTURE" | "MISSING"

export interface TimeCoverageDay {
  date: string
  state: TimeCoverageState
  /** Null where nothing was recorded — which is not the same claim as zero. */
  workedHours: string | null
  targetHours: string | null
  absenceName: string | null
  absenceColor: string | null
  absenceSpan: AbsenceSpan | null
  /** What the day's paid absences are worth against its Soll — already inside the account's Saldo. */
  creditHours?: string
  /** Every absence on the day, fullest first; the three fields above name the first of them. */
  absences?: TimeCoverageAbsence[]
}

/** One absence on one day — see the backend's `FrontendTimeCoverageAbsenceResponse`. */
export interface TimeCoverageAbsence {
  assignmentId: string
  absenceId: string
  name: string
  color: string
  paid: boolean
  span: AbsenceSpan
  /** "HH:mm:ss", only for a CUSTOM span. */
  fromTime: string | null
  toTime: string | null
  /** The assignment's whole span, unclipped. */
  from: string
  to: string
  /** Its share of the day's Soll, or null where the day owes nothing. */
  hours: string | null
}

/** What to do with days the new absence does not fit on — see `fitsBeside` in the backend. */
export type AbsenceConflictMode = "FAIL" | "SKIP" | "REPLACE"

/**
 * An absence entered from "Zeiten prüfen": one kind, one run of days, for one or more employees.
 * Stored server-side as the planning board's own `AbsenceAssignment`, so both screens show it.
 */
export interface TimeTrackingAbsenceRequest {
  employeeIds: string[]
  absenceId: string
  from: string
  to: string
  span: AbsenceSpan
  fromTime?: string | null
  toTime?: string | null
  onConflict?: AbsenceConflictMode
}

export interface TimeTrackingAbsenceConflict {
  userId: string
  date: string
  /** The kinds already on the day, by name. */
  existing: string[]
}

export interface TimeTrackingAbsenceResult {
  assignmentIds: string[]
  skipped: { userId: string; date: string }[]
  replaced: { userId: string; date: string }[]
}

export interface TimeCoverage {
  userId: string
  userName: string | null
  from: string
  to: string
  /** Empty when the caller asked for summaries only — the ranked Fehlzeiten list does. */
  days: TimeCoverageDay[]
  missingDays: number
  recordedDays: number
  expectedDays: number
  absentDays: number
  workedHours: string
  targetHours: string
  balanceHours: string
  oldestMissingDate: string | null
}

export interface TimeAccountMonth {
  month: string
  workedHours: string
  targetHours: string
  balanceHours: string
  /** The account after this month — the running total, so nobody has to add a column up. */
  closingBalanceHours: string
  recordedDays: number
}

/**
 * An employee's Arbeitszeitkonto — a *balance*, not a period figure.
 *
 * Somebody forty hours up does not become level because you asked about September, which is the
 * whole reason this is not the review table's Saldo column summed.
 */
export interface TimeAccount {
  userId: string
  userName: string | null
  from: string
  to: string
  /** The migrated seed, or null where the workspace never set one. */
  openingBalanceHours: string | null
  openingDate: string | null
  /** The balance carried **into** `from`. */
  carryOverHours: string
  workedHours: string
  targetHours: string
  balanceHours: string
  closingBalanceHours: string
  recordedDays: number
  months: TimeAccountMonth[]
}

/** Keep in sync with the backend `TimeComplianceRule`. */
export type TimeComplianceRule =
  | "DAILY_MAXIMUM_EXCEEDED"
  | "REST_PERIOD_TOO_SHORT"
  | "BREAK_TOO_SHORT"
  | "BREAK_NOT_DOCUMENTED"
  | "WEEKLY_MAXIMUM_EXCEEDED"
  | "OPEN_ENTRY_STALE"

export type TimeComplianceSeverity = "VIOLATION" | "WARNING"

export interface TimeComplianceFinding {
  userId: string
  userName: string | null
  date: string
  rule: TimeComplianceRule
  severity: TimeComplianceSeverity
  /** In hours, as numbers: the sentence is assembled in the SPA so it reads as a clock and translates. */
  actual: string
  limit: string
  entryIds: string[]
  workTimeModelName: string | null
}

export interface TimeCompliance {
  from: string
  to: string
  findings: TimeComplianceFinding[]
  violationCount: number
  warningCount: number
  affectedEmployeeCount: number
}

/** Keep in sync with the backend `FrontendTimeReportReconciliationPayload`. */
export type TimeReconciliationKind = "MISSING_REPORT" | "DEVIATION"

/**
 * One employee's day on one project where the recorded time and the project's reports disagree.
 *
 * `reportedHours` is what the reports say for that person and day: the hours of their line on every
 * Stundenlohnbericht plus the full duration of every Bautagesbericht shift they are listed on — a
 * Bautagesbericht carries no hours per person. The opposite case, reported but never clocked, is not
 * a row on purpose: the reports are what the project is costed from, and the question the section
 * answers is whether they are complete.
 */
export interface TimeReconciliationRow {
  userId: string
  userName: string | null
  date: string
  trackedHours: number
  reportedHours: number
  /** Tracked minus reported, so a positive value is time a report is missing. */
  differenceHours: number
  kind: TimeReconciliationKind
  hourlyWageReportIds: string[]
  constructionReportIds: string[]
}

export interface TimeReconciliation {
  from: string
  to: string
  /** How far the two may drift apart before a day is a DEVIATION; the server owns the number. */
  toleranceHours: number
  summary: {
    missingReportDays: number
    missingReportHours: number
    deviationDays: number
  }
  /** Newest day first. */
  rows: TimeReconciliationRow[]
}

/**
 * The two figures the Zeiterfassung's nav strip puts on a tab: how many people are behind on
 * recording, and how many Arbeitszeitgesetz breaches the workspace is carrying.
 *
 * Counts and nothing else. The lists behind them belong to the two screens that can act on them,
 * over whatever period and whichever employees those screens were asked about — see [loadNavCounts]
 * for why a badge cannot share either.
 */
export interface TimeNavCounts {
  behind: number
  violations: number
}

const headers = { headers: { tenant: true, authorization: true } }

/**
 * The latest request per review slot. "Zeiten prüfen" reloads on every tick of its picker, so
 * several reads of one slot are routinely in flight at once, and only the newest may write it: an
 * older answer arriving last would put the previous selection's rows, accounts or findings under
 * the new one. Bumped on the empty-selection path too, so a request still out cannot refill a slot
 * that was just emptied.
 */
const latestReviewRead = { entries: 0, accounts: 0, compliance: 0 }

/** This browser's wall clock as `HH:mm` — the walkthrough's stamps, which never reach the server. */
function demoClockTime(now = new Date()): string {
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
}

/** The frame's clock-in, closed now — see `clockOut`. */
function closeDemoClock(running: TimeEntry): TimeEntry {
  const now = new Date()
  const [hour, minute] = (running.fromTime ?? "00:00").split(":").map(Number)
  const minutes = Math.max(1, now.getHours() * 60 + now.getMinutes() - (hour * 60 + minute))
  const end = demoClockTime(now)
  return {
    ...running,
    toTime: end,
    // The same thing the server's clock-out does with a break nobody came back from.
    breaks: running.openBreakFrom
      ? [...running.breaks, { fromTime: running.openBreakFrom, toTime: end }]
      : running.breaks,
    openBreakFrom: null,
    status: "SUBMITTED",
    workedHours: (minutes / 60).toFixed(2),
    grossHours: (minutes / 60).toFixed(2),
  }
}

/**
 * How many employees the nav strip's badges are counted over — the same page size Zeiten prüfen fills
 * its employee picker with, so the badge is counted over the set that screen would have asked about.
 * A workspace past it loses the tail of the alphabet from a *count*, which is the cheap failure: the
 * screen itself is already capped the same way.
 *
 * It is also the size the request limits are set for. Every id goes into the query string, and 500 of
 * them is about 19.6 KB of URL. The edge nginx (`large_client_header_buffers`) and Tomcat
 * (`server.max-http-request-header-size`) both allow 32 KB. Raise this and both have to follow.
 */
const NAV_COUNT_EMPLOYEE_LIMIT = 500

/**
 * The IANA zone this browser is in — `Europe/Berlin` — sent with the two clock actions.
 *
 * A recorded day is a wall clock with no zone attached, and the server has no way to guess which one
 * to write it on: `LewaraApplication` pins the JVM's default to GMT, so a tap at 14:32 in Germany was
 * stamped 12:32. The running timer counts from that stamp against this browser's clock and so opened
 * at two hours, and near midnight the *date* was wrong too — a Tuesday morning booked to Monday.
 *
 * Only the zone travels, never a time: the instant stays the server's, so a client cannot decide when
 * its own working day began. Undefined where the browser will not say, and axios then omits it — the
 * server falls back to its own zone, exactly as before.
 */
const browserZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/**
 * The attendance as it just arrived, with every entry that has not changed since the last answer
 * **kept as the object it was** — or null when nothing changed at all, so there is nothing to assign.
 *
 * The Anwesenheitstafel re-reads itself every minute, and a response is always new objects. Assigned
 * as it came, every poll redrew every row of the board — a hundred and fifty people on the clock were
 * a hundred and fifty rows redrawn a minute, folded or not, to show what was already there. Kept, a
 * quiet minute redraws nothing and a clock-in redraws its own row: each row is a component
 * (`LewTimeAttendanceRow`) whose only props are the entry and the day.
 *
 * Compared as JSON, entry by entry: the same serializer wrote both, so equal data is equal text.
 */
function keepUnchangedEntries(previous: TimeEntry[], next: TimeEntry[]): TimeEntry[] | null {
  const before = new Map(previous.map(it => [it.id, toRaw(it)]))
  let changed = previous.length !== next.length
  const merged = next.map((entry, index) => {
    const old = before.get(entry.id)
    if (!old || JSON.stringify(old) !== JSON.stringify(entry)) {
      changed = true
      return entry
    }
    if (toRaw(previous[index]) !== old) changed = true
    return old
  })
  return changed ? merged : null
}

export const useTimeTrackingStore = defineStore("timeTracking", {
  state: () => ({
    /** The signed-in person's own days for the viewed week. */
    ownEntries: [] as TimeEntry[],
    /**
     * The open clock-in, held apart from [ownEntries] on purpose: it belongs to *today*, while
     * `ownEntries` holds whichever week is being looked at. Derived from the week list, a running
     * clock vanished the moment somebody paged back to last week — and the page then offered
     * to clock in again, which the backend can only answer with TIME_ENTRY_ALREADY_RUNNING.
     */
    running: null as TimeEntry | null,
    /** The first page of projects the caller may book their own day to — see [loadOwnProjects]. */
    ownProjects: [] as BookableProject[],
    /** Somebody else's days — the review screen. */
    reviewEntries: [] as TimeEntry[],
    /** One project's days — the project's Zeiten tab. Its own slot; see [loadForProject]. */
    projectEntries: [] as TimeEntry[],
    /** One project's recorded days against its reports — the same tab; see [loadProjectReconciliation]. */
    projectReconciliation: null as TimeReconciliation | null,
    /**
     * Everybody on the clock right now — the Anwesenheitstafel.
     *
     * Held apart from [running], which is the signed-in person's own and drives the clock panel.
     * The board carries no date window at all, on purpose: a Friday clock-out that never happened is
     * still running on Monday, and it is precisely the entry somebody opens the board to find.
     */
    attendance: [] as TimeEntry[],
    /** The caller's own, for the week dashboard's missing-day hint. */
    ownCoverage: null as TimeCoverage | null,
    accounts: [] as TimeAccount[],
    ownAccount: null as TimeAccount | null,
    compliance: null as TimeCompliance | null,
    /**
     * The two queue sizes the feature's nav strip badges — see [loadNavCounts].
     *
     * Their own slot rather than a reading of [coverage] and [compliance]: those hold whatever
     * window and whichever employees the screen on top asked for, and a badge that has to survive a
     * navigation cannot share a slot with a screen that rewrites it on every date change.
     */
    navCounts: { behind: 0, violations: 0 } as TimeNavCounts,
    /**
     * True in the app instance inside the Erklärung's frame, which shows a sample week.
     *
     * Only what the store has to *hold* is answered here: the own week and the clock, which the
     * walkthrough changes by clocking in and out (a write the sandbox refuses), and the loaders that
     * would otherwise overwrite the slots [enterHelpDemo] seeded — those go inert. Every other read — the
     * review rows, a project's days, coverage, the accounts, the compliance findings — goes through
     * `apiClient` like any other feature's and is answered by the sandbox from
     * `services/viewHelp/samples/timetracking.ts`, so a new loader is answered the same way everything
     * else in the frame is.
     */
    helpDemo: false,
  }),
  getters: {
    getOwnEntries: state => state.ownEntries,
    getOwnProjects: state => state.ownProjects,
    getReviewEntries: state => state.reviewEntries,
    getProjectEntries: state => state.projectEntries,
    getProjectReconciliation: state => state.projectReconciliation,
    getAttendance: state => state.attendance,
    getAccounts: state => state.accounts,
    getCompliance: state => state.compliance,
    getNavCounts: state => state.navCounts,
    /** The open clock-in, if the person has one. There is never more than one — the backend refuses it. */
    runningEntry: state => state.running,
  },
  actions: {
    // --- own time. No permission needed: the backend derives whose days these are from the token ---

    async loadOwn(from: string, to: string) {
      // In the walkthrough's frame the week is answered from the sample data rather than being inert:
      // the walkthrough pages back a week, and a page that kept the same days on screen after that
      // click would show paging as doing nothing. See `helpDemo`.
      if (this.helpDemo) {
        this.ownEntries = demoData().buildDemoOwnWeek(from, to)
        return
      }
      const res = await apiClient.get("/time-tracking/me", { params: { from, to }, ...headers })
      this.ownEntries = res.data

      // The loaded window is authoritative *for itself*: an open entry inside it is the running one,
      // and its absence only means something when the window covers the day the clock was started.
      // Anything else would clear the clock every time somebody looks at another week.
      const open = res.data.find((it: TimeEntry) => it.toTime === null) ?? null
      if (open) this.running = open
      else if (this.running && this.running.date >= from && this.running.date <= to) this.running = null
      return res
    },
    /**
     * Finds the open clock-in without touching the viewed week. The window reaches a week back rather
     * than to today: somebody who forgot to clock out on Friday still has a running day on Monday, and
     * that is precisely the case where the page must not offer to clock in again.
     *
     * It reaches *forward* a day as well, and that is not symmetry for its own sake. A recorded day
     * carries a plain date with no timezone, while the bound sent here is derived from the reader's
     * clock; somebody an hour ahead of the server, or looking at the page as midnight passes, would
     * otherwise ask for a window that stops just before the day their clock is running in.
     *
     * It clears the clock only for a day its own window actually covered — the same rule [loadOwn]
     * follows, and for the same reason. This runs beside [loadOwn] on the same endpoint, and an
     * unconditional `?? null` made the two race: whichever answered last won, so an open entry the
     * week list had correctly found was wiped a moment later by this one. Answering "not in the
     * range I asked about" with "there is none" is what turns a lost race into a lost clock.
     *
     * Both bounds stay as they are. This runs on every page load and at app start, so its window is
     * also the amount of somebody's history the product fetches to draw one chip; the entry it cannot
     * reach is [recoverRunning]'s job, on the one path that has been told such an entry exists.
     *
     * @param silent for the app-start load behind the toolbar's clock chip. Nobody asked for that
     *   read, and nothing on screen is missing without it — a snackbar over the dashboard about a
     *   feature the person may not even use is noise. Failures still reject, and `running` is left
     *   as it was, so the chip simply does not appear.
     */
    async loadRunning(silent = false) {
      if (this.helpDemo) return
      const to = new Date()
      to.setDate(to.getDate() + 1)
      const from = new Date()
      from.setDate(from.getDate() - 7)
      const fromIso = dateToNeutralString(from)
      const toIso = dateToNeutralString(to)
      const res = await apiClient.get("/time-tracking/me", {
        params: { from: fromIso, to: toIso },
        ...headers,
        silent,
      })
      const open = res.data.find((it: TimeEntry) => it.toTime === null) ?? null
      if (open) this.running = open
      else if (this.running && this.running.date >= fromIso && this.running.date <= toIso) this.running = null
      return this.running
    },
    /**
     * The forgotten clock-in, found however old it is — the one case [loadRunning] cannot answer.
     *
     * A clock-in refused with `TIME_ENTRY_ALREADY_RUNNING` while `running` is null means the open
     * entry is outside that seven-day window, and re-reading the same window a second time can only
     * come back empty again. The employee is then locked out for good: every tap is refused, the
     * panel keeps offering **Einstempeln**, and the open day is visible only on the
     * Anwesenheitstafel, which needs VIEW_TIME_ENTRY_FOREIGN to look at.
     *
     * A year back, and that bound belongs to this SPA alone: the server's own search
     * (`findByUserIdAndToTimeIsNull`) carries no date at all, so there is no age at which it stops
     * refusing the next clock-in. But `/me` is a window query, and asking with no lower bound would
     * download the person's entire recorded history to find a single row. A year is where forgetting
     * stops being the explanation — an open day has to survive the daily `OPEN_ENTRY_STALE` warning,
     * the Anwesenheitstafel and twelve Monatsabschlüsse to get that old — and the caller has to have
     * something to say for "still nothing" in any case.
     *
     * Unlike [loadOwn] and [loadRunning] it never *clears* the clock, and the wide window is exactly
     * why. Those two clear only for a day their own window covered, because they run concurrently on
     * the same endpoint and the loser of that race would otherwise wipe what the winner found; a
     * window covering every week anybody looks at would lose that protection entirely. There is
     * nothing to clear here anyway — this only ever runs on a refusal that proves an entry exists, so
     * an empty answer is a thing to tell the person, not a thing to write into the state.
     */
    async recoverRunning() {
      if (this.helpDemo) return
      const to = new Date()
      to.setDate(to.getDate() + 1)
      const from = new Date()
      from.setDate(from.getDate() - 365)
      const res = await apiClient.get("/time-tracking/me", {
        params: { from: dateToNeutralString(from), to: dateToNeutralString(to) },
        ...headers,
      })
      const open = res.data.find((it: TimeEntry) => it.toTime === null) ?? null
      if (open) this.running = open
      return open as TimeEntry | null
    },
    async createOwn(entry) {
      const res = await apiClient.post("/time-tracking/me", entry, headers)
      return res
    },
    async updateOwn(id: string, entry) {
      const res = await apiClient.put(`/time-tracking/me/${id}`, entry, headers)
      return res
    },
    async deleteOwn(id: string) {
      const res = await apiClient.delete(`/time-tracking/me/${id}`, headers)
      this.ownEntries = this.ownEntries.filter(it => it.id !== id)
      // Discarding an accidental clock-in is a delete, not a clock-out; the clock has to go with it.
      if (this.running?.id === id) this.running = null
      return res
    },
    async clockIn(projectId: string | null = null, phaseId: string | null = null) {
      // In the walkthrough's frame the clock runs here and nowhere else: the walkthrough clocks in to
      // show the clock in the app bar, and the sandbox would refuse the request. See `helpDemo`.
      if (this.helpDemo) {
        this.running = demoData().buildDemoClockIn(projectId)
        return { data: this.running }
      }
      const params: Record<string, string | undefined> = { zone: browserZone() }
      if (projectId) params.projectId = projectId
      if (phaseId) params.phaseId = phaseId
      const res = await apiClient.post("/time-tracking/me/clock-in", null, { params, ...headers })
      this.running = res.data
      return res
    },
    /**
     * Closes the running day. The response body is empty where the clock-in was undone rather than
     * closed (both taps inside the same second) — the caller has to tell those apart, because one is
     * "8:15 recorded" and the other is "nothing was recorded".
     */
    async clockOut() {
      // …and stops here: the day it closes is the sample's, a few minutes long and kept nowhere.
      if (this.helpDemo) {
        const closed = this.running ? closeDemoClock(this.running) : null
        this.running = null
        return { data: closed }
      }
      const wasRunning = this.running
      const res = await apiClient.post("/time-tracking/me/clock-out", null, {
        params: { zone: browserZone() },
        ...headers,
      })
      this.running = null

      // The week list is carried along, the same way [setStatus] splices its answers back in. The
      // clock can now be stopped from the app bar, on any page in the product, and that tap does not
      // know which week "Meine Zeiten" has open behind it — a row left reading "läuft", with the
      // week's Ist computed from it, would be the page disagreeing with the header above it. The
      // page still reloads after its own clock-out; this is for every other way the day gets closed.
      const closed = res.data?.id ? (res.data as TimeEntry) : null
      if (closed) {
        const index = this.ownEntries.findIndex(it => it.id === closed.id)
        if (index !== -1) this.ownEntries.splice(index, 1, closed)
      } else if (wasRunning) {
        // An empty body is the clock-in being undone: that entry no longer exists anywhere.
        this.ownEntries = this.ownEntries.filter(it => it.id !== wasRunning.id)
      }
      return res
    },
    /**
     * Stamps the start of a break on the running day.
     *
     * The clock had no break at all, and the day was charged one regardless: the working-time model
     * deducts the statutory minimum whether or not anybody recorded anything, so an ordinary clocked
     * day carried half an hour off with no times behind it. A deduction that cannot say *when* the
     * break was is a figure the workspace cannot defend — § 16 Abs. 2 ArbZG — and typing it in
     * afterwards is the step people skip.
     */
    async startBreak() {
      // Same rule as the clock itself in the walkthrough's frame: the stamp happens locally, on the
      // sample day, and the sandbox is never asked.
      if (this.helpDemo) {
        if (this.running) this.running = { ...this.running, openBreakFrom: demoClockTime() }
        return { data: this.running }
      }
      const res = await apiClient.post("/time-tracking/me/break-start", null, {
        params: { zone: browserZone() },
        ...headers,
      })
      this.running = res.data
      return res
    },
    /**
     * Ends it, which is what writes the break — with both its times — into the day.
     *
     * Two taps inside the same second leave nothing behind: that is a mis-tap, and the server reads
     * it the same way it reads a clock-out in the second it was clocked in.
     */
    async endBreak() {
      if (this.helpDemo) {
        if (this.running) this.running = { ...this.running, openBreakFrom: null }
        return { data: this.running }
      }
      const res = await apiClient.post("/time-tracking/me/break-end", null, {
        params: { zone: browserZone() },
        ...headers,
      })
      this.running = res.data
      return res
    },
    async suggestOwn(date: string): Promise<TimeEntrySuggestion> {
      const res = await apiClient.get("/time-tracking/me/suggest-project", { params: { date }, ...headers })
      return res.data
    },
    /**
     * The first page of projects the caller may book their own day to — the clock's and the entry
     * form's starting list on "Meine Zeiten".
     *
     * Not `GET /project`: that is the project *list*, gated on the project view rights, and a role that
     * only records time holds none of them. Which projects come back is the server's decision — the two
     * [BOOK_OWN_TIME_PERMISSIONS], plus the sites the personnel planning has put the caller on — which
     * is why it is asked even for a role holding neither right: the planned ones can still come back.
     */
    async loadOwnProjects(itemsPerPage: number) {
      const page = await this.searchOwnProjects({ itemsPerPage })
      this.ownProjects = page.items
      return this.ownProjects
    },
    /**
     * One page of bookable projects for a search term, **returned** rather than written into
     * [ownProjects] — the split `searchProjects` in the project store makes, for its reason: the
     * shared list is what both pickers on the page start from, so a keystroke in one must not narrow
     * the other. Silent for the same reason as there: it fires on typing. `page` for the picker
     * loading on as it is scrolled.
     */
    async searchOwnProjects({
      search,
      page = 0,
      itemsPerPage,
    }: {
      search?: string
      page?: number
      itemsPerPage?: number
    }) {
      const res = await apiClient.get("/time-tracking/me/projects", {
        params: { page, itemsPerPage, ...(search ? { search } : {}) },
        ...headers,
        silent: true,
      })
      // A `Slice`, so [hasMoreRows] reads its `last`; through the helper all the same, so the three
      // searches cannot drift apart on what "more" means.
      return { items: (res.data?.content ?? []) as BookableProject[], hasMore: hasMoreRows(res.data, itemsPerPage) }
    },

    // --- somebody else's time ------------------------------------------------------------------

    async loadFor(userIds: string[], from: string, to: string) {
      const seq = ++latestReviewRead.entries
      if (userIds.length === 0) {
        this.reviewEntries = []
        return null
      }
      const res = await apiClient.get("/time-tracking", {
        params: { userIds: userIds.join(","), from, to },
        ...headers,
      })
      if (seq === latestReviewRead.entries) this.reviewEntries = res.data
      return res
    },
    /**
     * The days these people were away. Held apart from [reviewEntries] because it answers the
     * opposite question — an entry is a day that happened, an absence day explains one that did not.
     */
    /**
     * A project's recorded days.
     *
     * Kept in its own slot rather than in `reviewEntries`, which it used to overwrite: the project
     * tab and the review screen are different pages about different questions, and a tab that empties
     * the reviewer's table behind it is a bug waiting for somebody to have both open.
     */
    async loadForProject(projectId: string, from: string, to: string) {
      // Emptied before the request, not only after it. The slot outlives the tab that fills it, so
      // opening the Zeiten tab of the *next* project rendered the previous one's totals and rollups
      // — under that project's name — for as long as the fetch took. Figures about the wrong project
      // are worse than a blank panel with a loading bar over it.
      this.projectEntries = []
      const res = await apiClient.get(`/time-tracking/project/${projectId}`, { params: { from, to }, ...headers })
      this.projectEntries = res.data
      return res
    },
    /**
     * Where a project's recorded days and its Stundenlohn- and Bautagesberichte disagree.
     *
     * Only called by a caller who may read both report types of the project: the endpoint answers 403
     * otherwise, and it has to, because a report it may not read would come back as "Bericht fehlt".
     * Emptied first for the same reason as [loadForProject] — the slot outlives the tab.
     */
    async loadProjectReconciliation(projectId: string, from: string, to: string) {
      this.projectReconciliation = null
      const res = await apiClient.get(`/time-tracking/project/${projectId}/reconciliation`, {
        params: { from, to },
        ...headers,
      })
      this.projectReconciliation = res.data
      return res
    },
    /**
     * The same prefill [suggestOwn] answers, for somebody else's day — one employee at a time.
     *
     * `{ silent: true }` because every caller is prefilling a form it must not take down: a
     * suggestion that cannot be read is a field left empty, not an error worth a snackbar. The
     * caller therefore has to tell a rejected promise ("could not ask") apart from a `NONE` source
     * ("asked, nothing to suggest") — those mean different things to a crew, where one employee's
     * silence must not outvote the others.
     *
     * Carries VIEW_TIME_ENTRY_FOREIGN, which every holder of EDIT_TIME_ENTRY_FOREIGN has by
     * dependency, so the crew dialog's callers can always ask.
     */
    async suggestFor(userId: string, date: string): Promise<TimeEntrySuggestion> {
      const res = await apiClient.get(`/time-tracking/suggest-project/${userId}`, {
        params: { date },
        ...headers,
        silent: true,
      })
      return res.data
    },
    /** One site day for a whole crew — how a workspace whose employees have no login records time. */
    async createForCrew(request) {
      // In the walkthrough's frame the Sammelerfassung is the one write the review demonstrates to the
      // end: the sandbox would refuse it, so the demo keeps the days and answers with them from then on.
      if (this.helpDemo) return { data: demoData().recordDemoCrew(request) }
      return apiClient.post("/time-tracking/crew", request, headers)
    },
    async updateAny(id: string, entry) {
      return apiClient.put(`/time-tracking/${id}`, entry, headers)
    },
    async deleteAny(id: string) {
      const res = await apiClient.delete(`/time-tracking/${id}`, headers)
      this.reviewEntries = this.reviewEntries.filter(it => it.id !== id)
      return res
    },

    // --- absences, entered from the review grid ---------------------------------------------------

    /**
     * No slot to update: an absence lives in the coverage the page holds itself, so the caller
     * reloads its period afterwards, exactly as it does after a crew entry.
     */
    async assignAbsence(request: TimeTrackingAbsenceRequest): Promise<TimeTrackingAbsenceResult> {
      const res = await apiClient.post("/time-tracking/absences", request, headers)
      return res.data
    },
    /** The days the request would not fit on — asked first, so a person chooses what happens to them. */
    async absenceConflicts(request: TimeTrackingAbsenceRequest): Promise<TimeTrackingAbsenceConflict[]> {
      const res = await apiClient.post("/time-tracking/absences/conflicts", request, headers)
      return res.data ?? []
    },
    /** Takes one day out of an absence; the backend shortens, splits or deletes it. */
    async removeAbsenceDay(assignmentId: string, date: string) {
      return apiClient.delete(`/time-tracking/absences/${assignmentId}/days/${date}`, headers)
    },

    // --- approval ------------------------------------------------------------------------------

    /**
     * @param note the reviewer's reason. It was hard-coded to null here and dropped on the floor by
     *   the backend, which made a rejection a dead end: the row turned red and the employee was told
     *   nothing. Blank is normalised to null so an untouched dialog does not store an empty string.
     */
    async setStatus(action: "approve" | "reject" | "reopen", entryIds: string[], note: string | null = null) {
      const body = { entryIds, note: note?.trim() ? note.trim() : null }
      const res = await apiClient.post(`/time-tracking/${action}`, body, headers)
      // Splice the returned rows back in rather than refetching the page: the server answers with
      // exactly the entries it changed, and the reviewer's filters and scroll position survive.
      res.data.forEach((updated: TimeEntry) => {
        const index = this.reviewEntries.findIndex(it => it.id === updated.id)
        if (index !== -1) this.reviewEntries.splice(index, 1, updated)
      })
      return res
    },

    // --- who is on the clock --------------------------------------------------------------------

    /**
     * @param silent for the Anwesenheitstafel's background poll. The board hangs on a wall and
     *   refreshes itself every minute; a snackbar for a hiccup nobody triggered — and which the next
     *   tick fixes — would be noise on a screen with no one in front of it. Failures still reject,
     *   and `attendance` is left as it was, so the previous list stays up.
     */
    async loadAttendance(silent = false) {
      if (this.helpDemo) return
      const res = await apiClient.get("/time-tracking/running", { ...headers, silent })
      const next = keepUnchangedEntries(this.attendance, res.data)
      if (next !== null) this.attendance = next
      return res
    },

    // --- coverage: which days are missing --------------------------------------------------------

    /**
     * The one read in this store that keeps **no slot of its own**, and hands the answer straight
     * back to its caller.
     *
     * It was written for three panels of "Zeiten prüfen" asking about three different windows on one
     * page, where a shared slot would have redrawn each as the others' calendar. The matrix is its
     * one caller now and holds the `res.data` it asked for — which is also what lets it drop an
     * answer that arrives after a newer one. An empty list of employees is answered with null and no
     * request at all, which the caller reads as "nothing to draw".
     *
     * @param includeDays false where only the per-employee counts are wanted: two hundred employees
     *   times ninety cells is a megabyte of calendar nobody renders. The counts come back either way.
     */
    async loadCoverage(userIds: string[], from: string, to: string, includeDays = true) {
      if (userIds.length === 0) return null
      return apiClient.get("/time-tracking/coverage", {
        params: { userIds: userIds.join(","), from, to, includeDays },
        ...headers,
      })
    },

    /**
     * The credit of the caller's paid absences per day of the week on screen, for its Saldo — read
     * off the own coverage, and handed back rather than stored: [ownCoverage] holds the month for the
     * missing-days banner, and a slot shared by the two would redraw one as the other.
     */
    async loadOwnCredits(from: string, to: string): Promise<Map<string, number>> {
      if (this.helpDemo) return new Map()
      const res = await apiClient.get("/time-tracking/me/coverage", { params: { from, to }, ...headers })
      return new Map((res.data?.days ?? []).map(day => [day.date, Number(day.creditHours) || 0]))
    },

    async loadOwnCoverage(from: string, to: string) {
      if (this.helpDemo) return
      const res = await apiClient.get("/time-tracking/me/coverage", { params: { from, to }, ...headers })
      this.ownCoverage = res.data ?? null
      return res
    },

    // --- Arbeitszeitkonto -------------------------------------------------------------------------

    async loadAccounts(userIds: string[], from: string, to: string) {
      const seq = ++latestReviewRead.accounts
      if (userIds.length === 0) {
        this.accounts = []
        return null
      }
      const res = await apiClient.get("/time-tracking/account", {
        params: { userIds: userIds.join(","), from, to },
        ...headers,
      })
      if (seq === latestReviewRead.accounts) this.accounts = res.data
      return res
    },

    async loadOwnAccount(from: string, to: string) {
      if (this.helpDemo) return
      const res = await apiClient.get("/time-tracking/me/account", { params: { from, to }, ...headers })
      this.ownAccount = res.data ?? null
      return res
    },

    // --- Arbeitszeitgesetz ------------------------------------------------------------------------

    async loadCompliance(userIds: string[], from: string, to: string) {
      const seq = ++latestReviewRead.compliance
      if (userIds.length === 0) {
        this.compliance = null
        return null
      }
      const res = await apiClient.get("/time-tracking/compliance", {
        params: { userIds: userIds.join(","), from, to },
        ...headers,
      })
      if (seq === latestReviewRead.compliance) this.compliance = res.data
      return res
    },

    // --- the nav strip's badges -------------------------------------------------------------------

    /**
     * The two figures `LewTimeTrackingNav` badges its tabs with, in one read, once per session.
     *
     * **The strip itself must not fetch.** It renders on both pages of the feature, so a request
     * inside it is a request per navigation. It reads [navCounts] as state instead, and this is
     * what fills them — from `main.js`, beside the running clock's load and for the same reason: a
     * count each page loads for itself is a badge you can only see once you no longer need it, which
     * is precisely the bug this replaces.
     *
     * **The window is the running month, and the employees are everybody the caller may see.** A
     * badge on every page has no screen to ask, so it has to pick. Month-to-date over the whole
     * staff is what Zeiten prüfen opens on, so on an untouched review (Arbeitszeit or Verstöße) the
     * badge and the page agree — and they stop agreeing the moment somebody narrows the picker or moves
     * the dates.
     * That is deliberate: the strip's figure stays the *workspace's* queue, because a marker meant to
     * pull you to another page cannot be scoped by the page you are standing on. The tooltip on the
     * chip says which window it means, so the two numbers are never silently the same name.
     *
     * Summaries only (`includeDays=false`): this counts people, and two hundred employees times a
     * month of cells is a calendar nobody is going to draw.
     *
     * Both reads are silent. Nobody asked for this one, so a failure has to leave the badges absent
     * rather than raise a snackbar over whatever page the person happens to be on — the caller
     * swallows the rejection and the counts stay where they were. Note that `silent` suppresses the
     * snackbar only: a 403 FEATURE_NOT_ENTITLED would still redirect, which is why the caller gates
     * on the add-on.
     *
     * The employee list comes through `searchEmployees`, which **returns** its page instead of
     * writing the employee store's shared list: an app-start read must not hand the screens a roster
     * they did not ask for, and it is silent for the same reason as the two reads below.
     */
    async loadNavCounts() {
      if (this.helpDemo) return
      const { items } = await useEmployeeStore().searchEmployees({
        search: null,
        itemsPerPage: NAV_COUNT_EMPLOYEE_LIMIT,
      })
      const userIds = items.map((it: { id: string }) => it.id)
      if (userIds.length === 0) {
        this.navCounts = { behind: 0, violations: 0 }
        return this.navCounts
      }

      const today = new Date()
      const params = {
        userIds: userIds.join(","),
        from: dateToNeutralString(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: dateToNeutralString(today),
      }
      const [coverage, compliance] = await Promise.all([
        apiClient.get("/time-tracking/coverage", {
          params: { ...params, includeDays: false },
          ...headers,
          silent: true,
        }),
        apiClient.get("/time-tracking/compliance", { params, ...headers, silent: true }),
      ])

      // Written into [navCounts] and nowhere else. Landing these in `coverage` and `compliance`
      // would put the workspace-wide figures under the screens that are about a chosen period, and
      // the first thing either screen does on mount would overwrite the badge.
      this.navCounts = {
        behind: (coverage.data ?? []).filter((it: TimeCoverage) => it.missingDays > 0).length,
        violations: compliance.data?.violationCount ?? 0,
      }
      return this.navCounts
    },

    // --- getting it out of the product ------------------------------------------------------------

    /**
     * Recorded time as a CSV.
     *
     * Blob-downloaded through `downloadFileFromXHR`, the same idiom as the master-data exports and
     * the invoice PDFs — an `<a href>` to the endpoint would carry no tenant or authorization header
     * and be answered with a 401.
     *
     * @param userIds empty for the caller's own days, which is a different route: `/me/export`
     *   derives its subject from the token and asks only for RECORD_OWN_TIME, like the rest of `/me`.
     */
    async exportCsv(userIds: string[], from: string, to: string, dialect = "GERMAN") {
      const own = userIds.length === 0
      const res = await apiClient.get(own ? "/time-tracking/me/export" : "/time-tracking/export", {
        params: own ? { from, to, dialect } : { userIds: userIds.join(","), from, to, dialect },
        responseType: "blob",
        ...headers,
      })
      downloadFileFromXHR(res.data, res.headers, `zeiterfassung_${from}_${to}.csv`)
      return res
    },

    /**
     * The Monatsnachweis for one employee and one month, as a PDF.
     *
     * @param userId null for the caller's own sheet. An employee may print the month they are asked
     *   to sign; anything else goes through the reviewer's route and its permission.
     * @param month ISO `yyyy-MM`.
     */
    async downloadMonthlyReport(userId: string | null, month: string) {
      const path = userId === null ? "/time-tracking/me/monthly-report" : `/time-tracking/monthly-report/${userId}`
      const res = await apiClient.get(path, { params: { month }, responseType: "blob", ...headers })
      downloadFileFromXHR(res.data, res.headers, `Monatsnachweis_${month}.pdf`)
      return res
    },

    /**
     * Fills this instance with the walkthrough's sample week.
     *
     * Only ever called at boot, and only in the instance inside the Erklärung's frame — see
     * `services/viewHelp/embed.ts`. That instance is a throwaway whose whole job is to be a filled-in
     * picture of the product, so there is nothing to put back afterwards and no counterpart to this:
     * the frame is discarded when the dialog closes.
     *
     * It writes state rather than shadowing getters because one of the slices these screens read —
     * `ownAccount` — is state, and a Pinia getter cannot shadow a state field of the same name.
     * Writing covers every slice the same way and needs no change in any view: everything on these
     * pages is derived from this store, so the tables, the tiles and the totals all fill in by
     * themselves.
     */
    async enterHelpDemo() {
      if (this.helpDemo) return
      demo = await import("@/services/viewHelp/demo")
      const sample = demo.buildDemoTimeTracking()

      this.ownEntries = sample.ownEntries
      this.running = sample.running
      this.reviewEntries = sample.reviewEntries
      this.attendance = sample.attendance
      this.navCounts = sample.navCounts
      this.ownAccount = sample.ownAccount
      // Set last, so the loaders only go inert once there is sample data for them to protect.
      this.helpDemo = true
    },
  },
})
